import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  EventType,
  VendorCategory,
  ExpenseStatus,
  PaymentMethod,
  CatalogItem,
  CatalogItemUsage,
  CreateCatalogItemInput,
  UpdateCatalogItemInput,
  FloorPlanObjectTypeConfig,
  CreateFloorPlanObjectTypeInput,
  UpdateFloorPlanObjectTypeInput,
  EventTypeDefaultTag,
  CreateEventTypeDefaultTagInput,
  UpdateEventTypeDefaultTagInput,
} from '@peles/shared';
import * as settingsApi from '../api/settings';

// ─── Event Types ─────────────────────────────────────────────────────────────

export function useSettingsEventTypes() {
  return useQuery<EventType[]>({
    queryKey: ['settings', 'eventTypes'],
    queryFn: () => settingsApi.fetchEventTypes(),
  });
}

export function useCreateEventType() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { name: string; nameHe?: string; description?: string }) =>
      settingsApi.createEventType(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'eventTypes'] });
    },
  });
}

export function useUpdateEventType() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      updates,
    }: {
      id: string;
      updates: {
        name?: string;
        nameHe?: string | null;
        description?: string | null;
        isActive?: boolean;
        sortOrder?: number;
      };
    }) => settingsApi.updateEventType(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'eventTypes'] });
    },
  });
}

// ─── Vendor Categories ───────────────────────────────────────────────────────

export function useSettingsVendorCategories() {
  return useQuery<VendorCategory[]>({
    queryKey: ['settings', 'vendorCategories'],
    queryFn: () => settingsApi.fetchVendorCategories(),
  });
}

export function useCreateVendorCategory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { name: string; nameHe?: string }) => settingsApi.createVendorCategory(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'vendorCategories'] });
    },
  });
}

export function useUpdateVendorCategory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      updates,
    }: {
      id: string;
      updates: {
        name?: string;
        nameHe?: string | null;
        isActive?: boolean;
        sortOrder?: number;
      };
    }) => settingsApi.updateVendorCategory(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'vendorCategories'] });
    },
  });
}

export function useDeleteVendorCategory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => settingsApi.deleteVendorCategory(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'vendorCategories'] });
    },
  });
}

// ─── Expense Statuses ────────────────────────────────────────────────────────

export function useSettingsExpenseStatuses() {
  return useQuery<ExpenseStatus[]>({
    queryKey: ['settings', 'expenseStatuses'],
    queryFn: () => settingsApi.fetchExpenseStatuses(),
  });
}

export function useCreateExpenseStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { name: string; nameHe?: string; color?: string; sortOrder?: number }) =>
      settingsApi.createExpenseStatus(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'expenseStatuses'] });
    },
  });
}

export function useUpdateExpenseStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      updates,
    }: {
      id: string;
      updates: {
        name?: string;
        nameHe?: string | null;
        color?: string | null;
        sortOrder?: number;
        isActive?: boolean;
      };
    }) => settingsApi.updateExpenseStatus(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'expenseStatuses'] });
    },
  });
}

// ─── Payment Methods ─────────────────────────────────────────────────────────

export function useSettingsPaymentMethods() {
  return useQuery<PaymentMethod[]>({
    queryKey: ['settings', 'paymentMethods'],
    queryFn: () => settingsApi.fetchPaymentMethods(),
  });
}

export function useCreatePaymentMethod() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { name: string; nameHe?: string; isProducerFunded?: boolean }) =>
      settingsApi.createPaymentMethod(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'paymentMethods'] });
    },
  });
}

export function useUpdatePaymentMethod() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      updates,
    }: {
      id: string;
      updates: {
        name?: string;
        nameHe?: string | null;
        isActive?: boolean;
        isProducerFunded?: boolean;
      };
    }) => settingsApi.updatePaymentMethod(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'paymentMethods'] });
    },
  });
}

// ─── Catalog Items ───────────────────────────────────────────────────────────

export function useSettingsCatalogItems() {
  return useQuery<CatalogItem[]>({
    queryKey: ['settings', 'catalogItems'],
    queryFn: () => settingsApi.fetchCatalogItems(),
  });
}

export function useCreateSettingsCatalogItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateCatalogItemInput) => settingsApi.createSettingsCatalogItem(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'catalogItems'] });
    },
  });
}

export function useUpdateSettingsCatalogItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: UpdateCatalogItemInput }) =>
      settingsApi.updateSettingsCatalogItem(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'catalogItems'] });
      queryClient.invalidateQueries({ queryKey: ['expenseConfig'] });
    },
  });
}

export function useCatalogItemUsage(id: string, enabled = true) {
  return useQuery<CatalogItemUsage>({
    queryKey: ['settings', 'catalogItems', id, 'usage'],
    queryFn: () => settingsApi.fetchCatalogItemUsage(id),
    enabled: enabled && !!id,
  });
}

export function useDeleteSettingsCatalogItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      reassignToCatalogItemId,
    }: {
      id: string;
      reassignToCatalogItemId: string | null;
    }) => settingsApi.deleteSettingsCatalogItem(id, reassignToCatalogItemId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'catalogItems'] });
      // Reassigned records live under other query roots
      queryClient.invalidateQueries({ queryKey: ['expenseConfig'] });
      queryClient.invalidateQueries({ queryKey: ['expenses'] });
      queryClient.invalidateQueries({ queryKey: ['planning-lists'] });
    },
  });
}

// ─── Floor Plan Object Types ────────────────────────────────────────────────

export function useSettingsFloorPlanObjectTypes() {
  return useQuery<FloorPlanObjectTypeConfig[]>({
    queryKey: ['settings', 'floorPlanObjectTypes'],
    queryFn: () => settingsApi.fetchFloorPlanObjectTypes(),
  });
}

export function useCreateFloorPlanObjectType() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateFloorPlanObjectTypeInput) =>
      settingsApi.createFloorPlanObjectType(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'floorPlanObjectTypes'] });
    },
  });
}

export function useUpdateFloorPlanObjectType() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: UpdateFloorPlanObjectTypeInput }) =>
      settingsApi.updateFloorPlanObjectType(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'floorPlanObjectTypes'] });
    },
  });
}

export function useDeleteFloorPlanObjectType() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => settingsApi.deleteFloorPlanObjectType(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'floorPlanObjectTypes'] });
    },
  });
}

// ─── Event Type Default Tags ─────────────────────────────────────────────────

export function useEventTypeDefaultTags(eventTypeId: string | undefined) {
  return useQuery<EventTypeDefaultTag[]>({
    queryKey: ['settings', 'eventTypes', eventTypeId, 'defaultTags'],
    queryFn: () => settingsApi.listEventTypeDefaultTags(eventTypeId!),
    enabled: !!eventTypeId,
  });
}

export function useCreateEventTypeDefaultTag(eventTypeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateEventTypeDefaultTagInput) =>
      settingsApi.createEventTypeDefaultTag(eventTypeId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['settings', 'eventTypes', eventTypeId, 'defaultTags'],
      });
    },
  });
}

export function useUpdateEventTypeDefaultTag(eventTypeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ tagId, updates }: { tagId: string; updates: UpdateEventTypeDefaultTagInput }) =>
      settingsApi.updateEventTypeDefaultTag(eventTypeId, tagId, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['settings', 'eventTypes', eventTypeId, 'defaultTags'],
      });
    },
  });
}

export function useDeleteEventTypeDefaultTag(eventTypeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (tagId: string) => settingsApi.deleteEventTypeDefaultTag(eventTypeId, tagId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['settings', 'eventTypes', eventTypeId, 'defaultTags'],
      });
    },
  });
}
